import { Dispatch, SetStateAction, useCallback } from 'react';
import { Typography, TypographyType } from '../../../../components';
import { ScorecardAnswer, ScorecardSectionType, TextColor } from '../../../../types';
import ScorecardQuestion from './ScorecardQuestion';

interface ScorecardContentProps {
  isEditMode: boolean;
  setAnswers: Dispatch<SetStateAction<ScorecardAnswer[] | undefined>>;
  sections?: ScorecardSectionType[];
  answers?: ScorecardAnswer[];
}

const ScorecardContent = ({ isEditMode, sections, answers, setAnswers }: ScorecardContentProps) => {
  const handleAnswerChange = useCallback((questionId: string, value: string) => {
    setAnswers((prevAnswers) => {
      // If there are no previous answers, create a new array with the current answer
      if (!prevAnswers) return [{ questionId, response: value }];

      let answerFound = false;
      // Map through existing answers, updating the matching question or keeping others as is
      const updatedAnswers = prevAnswers.map((answer) => {
        if (answer.questionId === questionId) {
          answerFound = true;
          return { ...answer, response: value }; // Update existing answer
        }
        return answer; // Keep other answers unchanged
      });

      // If no matching question was found, add the new answer to the array
      if (!answerFound) {
        updatedAnswers.push({ questionId, response: value });
      }

      // Return the updated answers array
      return updatedAnswers;
    });
  }, []);

  // Retrieves the answer for a specific question
  const getAnswerForQuestion = (questionId: string) => {
    return answers?.find((answer) => answer.questionId === questionId)?.response || '';
  };

  return (
    <div className="flex flex-col gap-6">
      {sections?.map((section) => {
        // Filter questions if in view mode to only show answered ones
        const questionsToDisplay = isEditMode
          ? section.questions
          : section.questions.filter((question) => getAnswerForQuestion(question.id));

        // Only show section if it has questions to display
        if (!questionsToDisplay.length) return null;

        return (
          <div key={section.id} className="flex flex-col gap-4">
            <Typography type={TypographyType.BASE} color={TextColor.SECONDARY}>
              {section.title}
            </Typography>
            <div className="flex flex-col gap-6 pl-4">
              {questionsToDisplay.map((question) => (
                <ScorecardQuestion
                  key={question.id}
                  answer={getAnswerForQuestion(question.id)}
                  onAnswerChange={isEditMode ? handleAnswerChange : undefined}
                  {...question}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ScorecardContent;
